"use client";

/**
 * RichTextRenderer — the full rich text block renderer.
 *
 * **Circular dependency:** This component and the portable block components
 * (PortableContentBlock, PortableNumberedBlock) form a circular dependency:
 *   RichText → PortableContentBlock/PortableNumberedBlock → RichText
 *
 * Importers reach this file through the `modules/RichText` re-export, so the
 * cycle passes through that re-export.
 *
 * Dynamic imports (`next/dynamic`) are used intentionally to break the import
 * cycle at runtime. This is a known architectural pattern, not a bug.
 *
 * @see docs/design-system-audit/03-dependency-analysis.md
 */
import { c as _c } from "react/compiler-runtime";
import { PortableText, type PortableTextComponents } from "next-sanity";
import React from "react";
import type { PortableTextBlock, TypedObject } from "@portabletext/types";
// Explicitly import the type from @portabletext/react which is used by next-sanity
import type { PortableTextTypeComponent } from "@portabletext/react";
import { RichTextContent, CtaBlock, ContentImage as SanityImageType } from "@/content/types";
import { ExternalLink } from "lucide-react";
import dynamic from "next/dynamic";
import { SanityImage } from "@/components/atoms/SanityImage";
import type { SanityImageSource } from "@/components/atoms/SanityImage";

// Static imports for components that don't cause circular dependencies
import PortablePullQuote from "./PortablePullQuote";
import PortableRelated from "./PortableRelated";
import PortableKeyPoints from "./PortableKeyPoints";
import PortableProcessSteps from "./PortableProcessSteps";
import PortableGlossary from "./PortableGlossary";
import PortableTwoColumn from "./PortableTwoColumn";
import { StaticCardGrid } from "@/components/modules/StaticCardGrid";
import { StaticCardGrid as StaticCardGridType } from "@/content/types";
import { PortableStatistics } from "@/components/modules/PortableStatistics";
import { Box } from "@/components/atoms/Box";
import { Link } from "@/components/atoms/Link";
import { Text } from "@/components/atoms/Text";
import { cn } from "@/lib/utils";

// Dynamic imports to resolve circular dependencies (RichText -> Component -> RichText)
// We cast to PortableTextTypeComponent because dynamic components return ComponentType<P> which doesn't perfectly match
// PortableTextTypeComponent (which expects index, isInline, etc. even if not used)
// This is safe because these components are just React components accepting the 'value' prop.
const PortableNumberedBlock = dynamic(() => import("./PortableNumberedBlock")) as unknown as PortableTextTypeComponent;
const PortableContentBlock = dynamic(() => import("./PortableContentBlock")) as unknown as PortableTextTypeComponent;
type Props = {
  data?: RichTextContent | CtaBlock["body"] | PortableTextBlock[] | null;
  /**
   * Cap the text column at a readable measure (--container-measure ≈ 72ch).
   * Set by the standalone `richText` module so body copy doesn't run the full
   * section width. Left unset when RichText fills a container that already
   * controls width (CTA panels, embedded portable text).
   */
  constrained?: boolean;
};
const PortableStaticCardGrid = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <Box className="not-prose my-12"><StaticCardGrid {...value} /></Box>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Renders a sanityImage block embedded in Portable Text content.
 * The image data arrives with asset dereferenced via PORTABLE_TEXT_EXTENSIONS.
 */
const PortableSanityImage = t0 => {
  const $ = _c(13);
  const {
    value
  } = t0;
  const t1 = value.asset as SanityImageSource["asset"];
  let t2;
  if ($[0] !== t1 || $[1] !== value.alt || $[2] !== value.caption || $[3] !== value.crop || $[4] !== value.hotspot) {
    t2 = {
      asset: t1,
      hotspot: value.hotspot,
      crop: value.crop,
      alt: value.alt,
      caption: value.caption
    };
    $[0] = t1;
    $[1] = value.alt;
    $[2] = value.caption;
    $[3] = value.crop;
    $[4] = value.hotspot;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const imageSource = t2;
  let t3;
  if ($[6] !== imageSource) {
    t3 = <SanityImage image={imageSource} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px" className="rounded-sm w-full h-auto" />;
    $[6] = imageSource;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== value.caption) {
    t4 = value.caption ? <Text as="p" className="mt-2 text-sm text-muted-foreground text-center">{value.caption}</Text> : null;
    $[8] = value.caption;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Box className="not-prose my-8">{t3}{t4}</Box>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

/**
 * Gracefully handles a malformed "span" block that ended up as a top-level
 * Portable Text block instead of an inline child. This can happen when content
 * is migrated or pasted incorrectly. Renders the text content inline.
 */
const PortableSpanFallback = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  if (!value.text) {
    return null;
  }
  let t1;
  if ($[0] !== value.text) {
    t1 = <Text>{value.text}</Text>;
    $[0] = value.text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const components: PortableTextComponents = {
  types: {
    sanityImage: PortableSanityImage,
    span: PortableSpanFallback,
    portablePullQuote: PortablePullQuote,
    portableNumberedBlock: PortableNumberedBlock,
    portableContentBlock: PortableContentBlock,
    portableRelated: PortableRelated,
    portableKeyPoints: PortableKeyPoints,
    portableProcessSteps: PortableProcessSteps,
    portableGlossary: PortableGlossary,
    staticCardGrid: PortableStaticCardGrid,
    portableStatistics: ({
      value
    }) => <PortableStatistics heading={value.heading} statistics={value.statistics} layout={value.layout} style={value.style} />,
    portableTwoColumn: PortableTwoColumn
  },
  marks: {
    link: ({
      children,
      value
    }) => {
      const href = value?.href || "";
      const isExternal = href.startsWith("http") || href.startsWith("//");
      const label = isExternal ? new URL(href, 'https://www.agencyq.com').hostname.replace('www.', '') : href;
      return <Link href={href} {...isExternal ? {
        target: "_blank",
        rel: "noreferrer noopener",
        "aria-label": label
      } : {
        "aria-label": label
      }} className="text-secondary underline decoration-secondary/50 hover:text-primary hover:decoration-primary/50 transition-colors">
          {children}
          {isExternal && <Text className="sr-only"> (opens in a new tab)</Text>}
        </Link>;
    },
    externalLink: ({
      children,
      value
    }) => {
      const rel = value.blank !== false ? "noreferrer noopener" : undefined;
      const target = value.blank !== false ? "_blank" : undefined;
      return <Link href={value.href} rel={rel} target={target} className="inline-flex items-center gap-1 text-secondary hover:text-primary transition-colors decoration-secondary/30 hover:decoration-primary/50">
          {children}
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          {target === "_blank" && <Text className="sr-only"> (opens in a new tab)</Text>}
        </Link>;
    }
  }
};

// Design blocks that own their own layout/width — a constrained prose measure
// must not clip or left-shift them (e.g. a centered pull-quote).
const SELF_LAID_OUT = new Set(["portablePullQuote", "sanityImage", "portableStatistics", "staticCardGrid"]);
const RichText = t0 => {
  const $ = _c(7);
  const {
    data,
    constrained
  } = t0;
  if (!data || data.length === 0) {
    return null;
  }
  const soleDesignBlock = data.length === 1 && !!data[0] && typeof data[0] === "object" && SELF_LAID_OUT.has((data[0] as {
    _type?: string;
  })._type ?? "");
  const t1 = constrained && !soleDesignBlock ? "mx-auto max-w-measure" : "max-w-none";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("prose dark:prose-invert", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = data as TypedObject[];
  let t4;
  if ($[2] !== t3) {
    t4 = <PortableText value={t3} components={components} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = <Box className={t2}>{t4}</Box>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
export default RichText;